import {
  createMigrate,
  PersistConfig,
  PersistedState,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import createFilter from 'redux-persist-transform-filter';

import { combineReducers, configureStore as reduxConfigureStore } from '@reduxjs/toolkit';
import { DEFAULT_WEEK_START } from '@utils/userConfig';

import * as auth from '../app/modules/Auth/_redux/authRedux.js';

import { validatePersistedSession } from './auth/validatePersistedSession.js';
import appConfigSlice from './features/appConfigSlice.js';
import authSlice, { type AuthState } from './features/authSlice.js';

export const rootReducer = combineReducers({
  auth: authSlice.reducer,
  appConfig: appConfigSlice.reducer,
  // LEGACY,
  legacyAuth: auth.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export type ConfigureStoreOptions = PersistConfig<RootState>;

// Persisted blobs are an untyped boundary (old app versions); narrow, don't trust the shape.
type PersistedRoot = PersistedState & { auth?: Partial<AuthState> };

// v2: back-fill `weekStart` for sessions persisted before it existed — autoMergeLevel1
// replaces the whole `auth` slice on rehydrate, so the initial-state default is lost.
const migrations = {
  2: (state: PersistedState): PersistedState => {
    const root = state as PersistedRoot;
    if (!root?.auth) return state;
    const migrated: PersistedRoot = {
      ...root,
      auth: { ...root.auth, weekStart: root.auth.weekStart ?? DEFAULT_WEEK_START },
    };
    return migrated;
  },
};

export const configureStore = <S extends RootState>(
  preloadedState?: S,
  options?: Partial<ConfigureStoreOptions>
) => {
  const defaultPersistConfig: PersistConfig<RootState> = {
    ...options,
    key: 'root',
    version: 2,
    storage: storage,
    transforms: [createFilter('cart')],
    migrate: createMigrate(migrations),
  };

  const persistedReducer = persistReducer(defaultPersistConfig, rootReducer);

  const store = reduxConfigureStore({
    devTools: import.meta.env.DEV,
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }),
  });

  // Validate the rehydrated auth slice once, in the bootstrap callback that fires
  // after rehydration completes and before `PersistGate` renders children (so
  // before any authenticated request). A stale pre-migration session (legacy /
  // non-JWT token, or missing refresh token) is reset to `authInitialState`,
  // flipping the route guard to unauthorized (SPAZ2-3203).
  const persistor = persistStore(store, null, () => {
    validatePersistedSession(store);
  });

  return {
    ...store,
    persistor,
  };
};

export type Store = ReturnType<typeof configureStore>;
export type StoreDispatch = Store['dispatch'];
export type StoreRootState = ReturnType<Store['getState']>;

export const actions = {
  ...authSlice.actions,
  ...appConfigSlice.actions,
};
export const selectAppConfig = (state: StoreRootState) => state.appConfig;
export const selectAuth = (state: StoreRootState) => state.auth;

export default configureStore;
